import type { ScheduleEventData } from "@mantine/schedule";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { getRootDispatcher } from "@/analytics";
import { env } from "@/config";

import type { ScheduleResponse } from "./types";

import { getBusinessToday } from "./businessDay";
import { EXPERT_HUB_ACTIONS } from "./expertHubErrorMetric";
import { getSchedule, scheduleHasShift } from "./getSchedule";
import { getMockSchedule } from "./mocks";
import { toScheduleEvents } from "./scheduleEvents";

export interface ScheduleResult {
    schedule: ScheduleResponse | null;
    /** AgendaView events derived from the schedule (one per block). */
    events: ScheduleEventData[];
    /** The business day being shown (YYYY-MM-DD) — pass to AgendaView `rangeStart`/`rangeEnd`. */
    date: string;
    isLoading: boolean;
    isError: boolean;
    /** True when `schedule` is dev mock data (non-prod fallback), for a "mock" indicator. Always false in prod. */
    isMock: boolean;
    /** True when there's a working window or at least one block — drives whether the off-call panel opens. */
    hasShift: boolean;
}

export interface UseScheduleOptions {
    /** Gate the underlying query (e.g. only fetch off-call). Defaults to true. */
    enabled?: boolean;
    /** Report the `ScheduleReceived` analytics once (opt-in so only one consumer fires it). */
    reportScheduleReceived?: boolean;
}

export function useSchedule({
    enabled = true,
    reportScheduleReceived = false,
}: UseScheduleOptions = {}): ScheduleResult {
    const date = getBusinessToday();

    const { data, isLoading, isError } = useQuery({
        queryKey: ["expertHub", "schedule", date],
        queryFn: () => getSchedule(date),
        staleTime: 30 * 60_000,
        refetchInterval: 30 * 60_000,
        retry: 2,
        enabled,
        // Reject signal → QueryCache.onError (see queryServices); bad-shape + empty → getSchedule.
        meta: { expertHubErrorAction: EXPERT_HUB_ACTIONS.scheduleFetchFailed },
    });

    // Memoized so `events` keeps a stable reference for AgendaView. Loading returns null so the
    // skeleton shows (no mock flash); non-prod falls back to the mock when the backend has no shift.
    const { schedule, isMock } = useMemo(() => {
        // Disabled query never runs — report no schedule (don't fall through to the non-prod mock).
        if (!enabled) return { schedule: null, isMock: false };
        if (env.isProdMode) return { schedule: data ?? null, isMock: false };
        if (isLoading) return { schedule: null, isMock: false };
        if (data && scheduleHasShift(data)) return { schedule: data, isMock: false };
        return { schedule: getMockSchedule(date), isMock: true };
    }, [enabled, data, isLoading, date]);

    const events = useMemo(() => toScheduleEvents(schedule), [schedule]);

    const hasShift = !!schedule && scheduleHasShift(schedule);

    // Report the impression once per mount for real (non-mock) data — refetches don't re-fire it.
    const reportedRef = useRef(false);
    useEffect(() => {
        if (!reportScheduleReceived || isMock || !schedule || reportedRef.current) return;
        reportedRef.current = true;
        void getRootDispatcher().dispatchBusinessEvent("ScheduleReceived", { hasShift });
    }, [reportScheduleReceived, isMock, schedule, hasShift]);

    return { schedule, events, date, isLoading, isError, isMock, hasShift };
}
